import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";

import imgA from "../../assets/images/project-01/a.jpg";
import imgB from "../../assets/images/project-01/b.jpg";
import imgC from "../../assets/images/project-01/c.jpg";
import imgD from "../../assets/images/project-01/d.jpg";
import imgE from "../../assets/images/project-01/e.jpg";
import imgF from "../../assets/images/project-01/f.jpg";
import imgG from "../../assets/images/project-01/g.jpg";
import imgH from "../../assets/images/project-01/h.jpg";
import imgI from "../../assets/images/project-01/i.jpg";
import imgJ from "../../assets/images/project-01/j.jpg";
import imgK from "../../assets/images/project-01/k.jpg";

import img2A from "../../assets/images/project-02/692610362_1761413565111564_5728057446475933120_n.jpg";
import img2B from "../../assets/images/project-02/690319483_1552177536471849_2947015809891223140_n.jpg";

import img3A from "../../assets/images/project-03/11dbddc2-3d7b-4814-bf2e-c3ebbc150fea.jpg";
import img3B from "../../assets/images/project-03/19d619fd-b48c-4cab-89d9-c28c0fafd047.jpg";
import img3C from "../../assets/images/project-03/64b1e552-6ef0-403f-a5e2-1aa5bf50e924.jpg";
import img3D from "../../assets/images/project-03/9a7df356-edc2-4d72-8048-a2e13b6ef8ff.jpg";
import img3E from "../../assets/images/project-03/f435cad1-6ee9-41cb-8dec-ff2325861fb9.jpg";

import vid2A from "../../assets/images/project-02/AQOx7zUVVvpNfEWFneiGjTezwdS4RPUrXSSHvC8z-QL-0mWuikff42P0D6d9lN9amO5tFIcIRtBrbWQC8Mrpg3UtZTuyEXOd7e8rIt2GPA.mp4";

/* Treat any media URL ending in a video extension as a <video>. */
const isVideo = (src) => /\.(mp4|webm|mov|m4v|ogg)$/i.test(src);

/* ---------- Lightbox with portal ---------- */
function Lightbox({ images, index, onClose, onPrev, onNext }) {
  const isOpen = index >= 0;

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose, onPrev, onNext]);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = prev;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <motion.div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-md"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      onClick={onClose}
    >
      <motion.div
        className="relative max-w-[92vw] max-h-[85svh] flex items-center justify-center"
        initial={{ scale: 0.98, y: 8 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 220, damping: 24 }}
        onClick={(e) => e.stopPropagation()}
      >
        {isVideo(images[index]) ? (
          <video
            key={images[index]}
            src={images[index]}
            className="max-h-[85svh] max-w-[92vw] rounded-xl shadow-2xl bg-black"
            controls
            autoPlay
            playsInline
          />
        ) : (
          <img
            src={images[index]}
            alt={`Project photo ${index + 1}`}
            className="max-h-[85svh] max-w-[92vw] object-contain rounded-xl shadow-2xl"
            decoding="async"
            draggable={false}
          />
        )}

        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-white/80 text-sm px-2 py-1 rounded bg-black/40">
          {index + 1} / {images.length}
        </div>

        <button
          onClick={onClose}
          className="absolute top-3 right-3 h-9 w-9 rounded-full bg-black/60 text-white grid place-items-center hover:bg-black/80"
          aria-label="Close"
        >
          ×
        </button>
        <button
          onClick={onPrev}
          className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/60 text-white text-xl grid place-items-center hover:bg-black/80"
          aria-label="Previous"
        >
          ‹
        </button>
        <button
          onClick={onNext}
          className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/60 text-white text-xl grid place-items-center hover:bg-black/80"
          aria-label="Next"
        >
          ›
        </button>
      </motion.div>
    </motion.div>,
    document.body
  );
}

/* -------------------------------- Projects -------------------------------- */
export default function Projects() {
  const project01 = [
    imgA,
    imgB,
    imgC,
    imgD,
    imgE,
    imgF,
    imgG,
    imgH,
    imgI,
    imgJ,
    imgK,
  ];

  const albums = [
    {
      id: "project-01",
      title: "Residential Rooftop — Grid-Tied",
      cover: imgA,
      images: project01,
      tag: "Residential",
      system: "10 kW · Grid-Tied",
    },
    {
      id: "project-02",
      title: "Residential Installation",
      cover: img2A,
      images: [img2A, img2B, vid2A],
      tag: "Residential",
      system: "16kw Hybrid Solar System",
    },
    {
      id: "project-03",
      title: "Residential Installation",
      cover: img3A,
      images: [img3A, img3B, img3C, img3D, img3E],
      tag: "Residential",
      system: "8kw Hybrid Solar System",
    },
  ];

  const [activeAlbum, setActiveAlbum] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  const openAlbum = (albumIdx) => {
    setActiveAlbum(albumIdx);
    setLightboxIndex(0);
  };
  const closeLightbox = () => {
    setLightboxIndex(-1);
    setActiveAlbum(null);
  };
  const next = () => {
    if (activeAlbum === null) return;
    setLightboxIndex((i) => (i + 1) % albums[activeAlbum].images.length);
  };
  const prev = () => {
    if (activeAlbum === null) return;
    const len = albums[activeAlbum].images.length;
    setLightboxIndex((i) => (i - 1 + len) % len);
  };

  const scrollerRef = useRef(null);
  const scrollBy = (dir) => {
    const node = scrollerRef.current;
    if (!node) return;
    node.scrollBy({
      left: Math.round(node.clientWidth * 0.9) * dir,
      behavior: "smooth",
    });
  };

  return (
    <section id="projects" className="relative w-full font-sora -mt-px">
      {/* Background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-section" />
      </div>

      {/* Content */}
      <div className="py-20 md:py-28 px-5 sm:px-8 md:px-10 text-text-primary">
        <div className="mx-auto max-w-[92rem]">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="font-sharetech text-4xl md:text-5xl font-bold mb-3 text-text-primary">Our Work</h2>
            <div className="h-1 w-32 bg-accent-sun/40 rounded-full mx-auto mb-4" />
            <p className="text-lg text-text-body max-w-2xl mx-auto">
              A look at the installations we've completed — each one built to last.
            </p>
          </motion.div>

          <motion.div
            className="relative group/rail"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            viewport={{ once: true }}
          >
            {/* rail arrows */}
            <button
              onClick={() => scrollBy(-1)}
              className="hidden md:grid opacity-0 md:group-hover/rail:opacity-100 transition-opacity absolute left-2 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-accent-sun hover:bg-orange-600 ring-1 ring-accent-sun/50 text-white place-items-center font-bold text-lg"
              aria-label="Scroll left"
            >
              ‹
            </button>
            <button
              onClick={() => scrollBy(1)}
              className="hidden md:grid opacity-0 md:group-hover/rail:opacity-100 transition-opacity absolute right-2 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-solar-gold hover:bg-solar-gold-deep ring-1 ring-solar-gold/50 text-solar-midnight place-items-center font-bold text-lg"
              aria-label="Scroll right"
            >
              ›
            </button>

            {/* scroller */}
            <div
              ref={scrollerRef}
              className={[
                "overflow-x-auto overscroll-x-contain",
                "snap-x snap-mandatory scroll-pl-4 pr-4 py-1",
                "[-ms-overflow-style:none] [scrollbar-width:none]",
                "[&::-webkit-scrollbar]:hidden",
              ].join(" ")}
            >
              <div className="flex gap-4">
                {albums.map((alb, idx) => (
                  <motion.button
                    key={alb.id}
                    onClick={() => openAlbum(idx)}
                    className="group/card relative snap-start shrink-0 w-[min(78vw,24rem)] aspect-[4/3] overflow-hidden rounded-2xl ring-2 ring-border-light hover:ring-accent-sun bg-card text-left transition"
                    whileHover={{ y: -6, scale: 1.02 }}
                    transition={{ type: "spring", stiffness: 300, damping: 22 }}
                  >
                    <img
                      src={alb.cover}
                      alt={alb.title || "Project gallery"}
                      className="h-full w-full object-cover transition-transform duration-300 group-hover/card:scale-110 select-none"
                      decoding="async"
                      draggable={false}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    {alb.images.some(isVideo) && (
                      <div className="absolute inset-0 grid place-items-center">
                        <span className="grid h-14 w-14 place-items-center rounded-full bg-white/85 text-accent-sun shadow-lg backdrop-blur-sm transition-transform group-hover/card:scale-110">
                          <svg viewBox="0 0 24 24" className="h-6 w-6 translate-x-0.5" fill="currentColor" aria-hidden="true">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </span>
                      </div>
                    )}
                    <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full text-xs font-bold bg-accent-sun/90 text-white uppercase tracking-wide">
                      {alb.tag}
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="text-white font-bold text-lg drop-shadow-lg">
                        {alb.title}
                      </div>
                      <div className="text-text-muted text-sm mt-1">
                        {alb.system}
                      </div>
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {activeAlbum !== null && lightboxIndex >= 0 && (
        <Lightbox
          images={albums[activeAlbum].images}
          index={lightboxIndex}
          onClose={closeLightbox}
          onPrev={prev}
          onNext={next}
        />
      )}
    </section>
  );
}
